import { cn } from '@/lib/utils';

type Props = {
  size?: number;
  /** Stroke thickness in pixels. Defaults to size / 12 (rounded). */
  strokeWidth?: number;
  /** Optional ARIA label. Falls back to "Loading". */
  label?: string;
  className?: string;
};

/**
 * Two complementary arcs (primary on top-right, primary-5 on bottom-left)
 * rotating continuously in opposite directions. The eye reads the chasing
 * arcs as a "filling" animation without ever requiring a discontinuous
 * loop boundary the way a clip-path fill does.
 *
 * @category Loaders
 */
export const RotatingLoader = ({
  size = 24,
  strokeWidth,
  label = 'Loading',
  className,
}: Props) => {
  const stroke = strokeWidth ?? Math.max(2, Math.round(size / 12));
  return (
    <span
      role="status"
      aria-label={label}
      className={cn('inline-block relative', className)}
      style={{ width: size, height: size }}
    >
      <span
        className="absolute inset-0 rounded-full border-transparent border-t-primary border-r-primary animate-spin"
        style={{ borderWidth: stroke }}
      />
      <span
        className="absolute inset-0 rounded-full border-transparent border-b-primary-5 border-l-primary-5"
        style={{
          borderWidth: stroke,
          animation: 'spin 1.4s linear infinite reverse',
        }}
      />
    </span>
  );
};
